import React, { useEffect, useState } from 'react';
import { useLoaderData, useParams, Link } from 'react-router';
import matter from 'gray-matter';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import 'highlight.js/styles/github-dark.css';
import Layout from '@/components/Layout';
import { EyeIcon } from 'lucide-react';
import { canonicalUrlFromRequest } from '@/lib/seo';
import { formatDate } from '@/lib/utils';

const extractText = (children: React.ReactNode): string => {
  if (typeof children === 'string') return children;
  if (typeof children === 'number') return String(children);
  if (Array.isArray(children)) return children.map(extractText).join('');
  if (React.isValidElement(children)) {
    const props = children.props as { children?: React.ReactNode };
    if (props.children) return extractText(props.children);
  }
  return '';
};

const slugify = (text: string) =>
  text
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-');

const headingComponents = {
  h1: ({ children }: { children?: React.ReactNode }) => (
    <h1 id={slugify(extractText(children))}>{children}</h1>
  ),
  h2: ({ children }: { children?: React.ReactNode }) => (
    <h2 id={slugify(extractText(children))}>{children}</h2>
  ),
  h3: ({ children }: { children?: React.ReactNode }) => (
    <h3 id={slugify(extractText(children))}>{children}</h3>
  ),
  h4: ({ children }: { children?: React.ReactNode }) => (
    <h4 id={slugify(extractText(children))}>{children}</h4>
  ),
};

export const loader = async ({
  params,
  request,
}: {
  params: { slug: string };
  request: Request;
}) => {
  const modules = import.meta.glob('../blogs/*.md', { query: '?raw', import: 'default' });
  const filePath = `../blogs/${params.slug}.md`;
  const resolver = modules[filePath];
  if (!resolver) return { notFound: true };
  const content = (await resolver()) as string;
  const { data, content: mdContent } = matter(content);

  // Generate table of contents from markdown headings with proper IDs
  const tocMatches = Array.from(mdContent.matchAll(/^##? (.+)$/gm));
  const toc = tocMatches.map((match) => {
    const text = match[1]
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
      .replace(/\*+/g, '')
      .replace(/\\(.)/g, '$1');
    const id = text
      .toLowerCase()
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, '-');
    return { text, id };
  });

  // Get other blogs for recommendations (excluding current blog)
  const allBlogs = await Promise.all(
    Object.entries(modules)
      .filter(([path]) => path !== filePath)
      .map(async ([path, resolver]) => {
        const content = (await resolver()) as string;
        const { data } = matter(content);
        const slug = path.replace('../blogs/', '').replace('.md', '');
        return {
          slug,
          title: data.title,
          author: data.author,
          date: data.date,
          image: data.image,
        };
      })
  );

  // Get 3 random recommendations
  const recommendations = allBlogs.sort(() => Math.random() - 0.5).slice(0, 3);

  // Canonical (query-param free) URL for meta tags
  const fullUrl = canonicalUrlFromRequest(request.url);

  return {
    title: data.title,
    author: data.author,
    date: data.date,
    image: data.image,
    description: data.description,
    content: mdContent,
    toc,
    recommendations,
    notFound: false,
    fullUrl,
  };
};

export function meta({ data }: { data: any }) {
  if (!data || data.notFound) {
    return [
      { title: 'Blog Not Found | Simulations Labs' },
      { name: 'description', content: 'The blog you are looking for could not be found.' },
      { name: 'robots', content: 'noindex, nofollow' },
    ];
  }

  // Create absolute URL for the image
  const imageUrl = data.image.startsWith('http')
    ? data.image
    : `https://simulationslabs.com${data.image}`;

  // Use frontmatter description if available, otherwise extract from content
  const description = data.description
    ? data.description
    : data.content
      ? data.content
          .replace(/[#*`\n]/g, ' ')
          .trim()
          .substring(0, 160) + '...'
      : 'Read this blog post on Simulations Labs';

  return [
    { title: `${data.title} | Simulations Labs` },
    { name: 'description', content: description },
    { name: 'author', content: data.author || 'Simulations Labs' },
    { name: 'robots', content: 'index, follow, max-image-preview:large' },
    { name: 'keywords', content: 'cybersecurity, Simulations Labs, security training' },

    // Canonical URL is emitted once, sitewide, in root.tsx

    // Open Graph tags for general social sharing
    { property: 'og:title', content: data.title },
    { property: 'og:description', content: description },
    { property: 'og:url', content: data.fullUrl },
    { property: 'og:image', content: imageUrl },
    { property: 'og:image:alt', content: data.title },
    { property: 'og:image:width', content: '1200' },
    { property: 'og:image:height', content: '630' },
    { property: 'og:type', content: 'article' },
    { property: 'og:site_name', content: 'Simulations Labs' },
    { property: 'og:locale', content: 'en_US' },

    // Article specific Open Graph tags
    { property: 'article:published_time', content: new Date(data.date).toISOString() },
    { property: 'article:author', content: data.author || 'Simulations Labs' },
    { property: 'article:section', content: 'Cybersecurity' },

    // Twitter Card tags
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: data.title },
    { name: 'twitter:description', content: description },
    { name: 'twitter:image', content: imageUrl },
    { name: 'twitter:image:alt', content: data.title },
    { name: 'twitter:site', content: '@SimulationsLabs' },
    { name: 'twitter:creator', content: '@SimulationsLabs' },

    // LinkedIn specific tags (uses Open Graph)
    { property: 'og:image:secure_url', content: imageUrl },
  ];
}

const shareLinks = [
  {
    icon: 'linkedin',
    url: (title: string, url: string) =>
      `https://www.linkedin.com/shareArticle?mini=true&title=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}`,
  },
  {
    icon: 'twitter',
    url: (title: string, url: string) =>
      `https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}`,
  },
  {
    icon: 'email',
    url: (title: string, url: string) =>
      `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(url)}`,
  },
  {
    icon: 'link',
    url: (title: string, url: string) => url,
  },
];

const BlogsDetailPage = () => {
  const { slug } = useParams();
  const { title, author, date, image, description, content, toc, notFound, recommendations } =
    useLoaderData() as any;
  const [currentUrl, setCurrentUrl] = useState('');
  const [activeHeading, setActiveHeading] = useState('');
  const [copied, setCopied] = useState(false);
  const [views, setViews] = useState<number | null>(null);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    fetch(`/api/views?path=${encodeURIComponent(`/blogs/${slug}`)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!cancelled && d && typeof d.views === 'number') setViews(d.views);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [slug]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setCurrentUrl(window.location.href);

      // Handle scroll to update active heading
      const handleScroll = () => {
        const headings = document.querySelectorAll('h1[id], h2[id], h3[id], h4[id]');
        let current = '';

        headings.forEach((heading) => {
          const rect = heading.getBoundingClientRect();
          if (rect.top <= 100) {
            current = heading.id;
          }
        });

        setActiveHeading(current);
      };

      window.addEventListener('scroll', handleScroll);
      handleScroll(); // Call once to set initial state

      return () => window.removeEventListener('scroll', handleScroll);
    }
  }, []);

  if (notFound) {
    return (
      <Layout>
        <div className="mx-auto max-w-2xl py-20 text-center">
          <h1 className="mb-4 text-3xl font-bold">Article Not Found</h1>
          <p className="mb-6">Sorry, we couldn't find the article you were looking for.</p>
          <Link to="/blogs" className="text-blue-600 underline">
            Back to Blogs
          </Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="mx-auto max-w-7xl px-4 py-10">
        {/* Breadcrumb */}
        <div className="mb-8 flex items-center gap-2 text-sm text-gray-500">
          <Link to="/blogs" className="font-medium text-blue-600 hover:underline">
            Blogs
          </Link>
          <span className="mx-2">&gt;</span>
          <span className="truncate font-medium text-gray-700">{title}</span>
        </div>

        {/* Article Header */}
        <div className="mb-10">
          <h1 className="mb-4 text-4xl leading-tight font-bold text-gray-900 lg:text-5xl">
            {title}
          </h1>
          <div className="flex items-center gap-6 text-sm text-gray-600">
            <div className="flex items-center gap-2">
              <span className="font-medium">{author}</span>
            </div>
            <div className="flex items-center gap-1">
              <span role="img" aria-label="date">
                📅
              </span>
              <span>{formatDate(date, 'long')}</span>
            </div>
            {views !== null && (
              <div className="flex items-center gap-1">
                <span role="img" aria-label="views">
                  <EyeIcon className="h-4 w-4" />
                </span>
                <span>{views.toLocaleString('en-US')} views</span>
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-10 lg:flex-row">
          {/* Main Content */}
          <div className="min-w-0 flex-1">
            {/* Featured Image */}
            <div className="mb-10">
              <img
                src={image}
                alt={title}
                className="w-full rounded-xl object-cover shadow-lg"
                style={{ maxHeight: '400px' }}
              />
            </div>

            {/* Article Content */}
            <article className="prose prose-lg prose-gray prose-headings:text-gray-900 prose-headings:font-bold prose-p:text-gray-700 prose-p:leading-relaxed prose-li:text-gray-700 prose-strong:text-gray-900 prose-a:text-blue-600 prose-a:no-underline hover:prose-a:underline prose-blockquote:border-blue-200 prose-blockquote:bg-blue-50 prose-blockquote:text-gray-700 max-w-none">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[[rehypeHighlight, { detect: true }]]}
                components={headingComponents}
              >
                {content || ''}
              </ReactMarkdown>
            </article>

            {/* Recommended Articles */}
            {recommendations && recommendations.length > 0 && (
              <div className="mt-16 border-t border-gray-200 pt-16">
                <h2 className="mb-8 text-2xl font-bold text-gray-900">Recommended Articles</h2>
                <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                  {recommendations.map((blog: any) => (
                    <Link
                      key={blog.slug}
                      to={`/blogs/${blog.slug}`}
                      className="group overflow-hidden border border-gray-200 bg-white shadow-sm transition-all duration-200 hover:border-gray-300 hover:shadow-lg"
                    >
                      <div className="aspect-video overflow-hidden">
                        <img
                          src={blog.image}
                          alt={blog.title}
                          className="h-full w-full object-cover transition-transform duration-200 group-hover:scale-105"
                        />
                      </div>
                      <div className="p-4">
                        <h3 className="mb-2 line-clamp-2 text-lg font-semibold text-gray-900 transition-colors group-hover:text-blue-600">
                          {blog.title}
                        </h3>
                        <div className="flex items-center gap-3 text-sm text-gray-600">
                          <div className="flex items-center gap-1">
                            <span className="font-medium">{blog.author}</span>
                          </div>
                          <span>•</span>
                          <span>{formatDate(blog.date)}</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <aside className="w-full flex-shrink-0 lg:w-80">
            {/* Table of Contents */}
            {toc.length > 0 && (
              <div className="sticky top-6 mb-8 rounded-xl border border-gray-200 bg-gray-50 p-6">
                <div className="mb-4 flex items-center gap-2">
                  <span className="text-lg">📋</span>
                  <h3 className="font-semibold text-gray-900">Table of Contents</h3>
                </div>
                <ul className="max-h-[60vh] space-y-2 overflow-y-auto text-sm">
                  {toc.map((heading: { text: string; id: string }, index: number) => (
                    <li key={heading.id} className="group">
                      <a
                        href={`#${heading.id}`}
                        className={`block truncate rounded px-2 py-1 text-gray-600 transition-colors duration-200 hover:bg-blue-50 hover:text-blue-600 ${activeHeading === heading.id ? 'bg-blue-50 text-blue-600' : ''}`}
                      >
                        {heading.text}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Share Article */}
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-6">
              <div className="mb-4 flex items-center gap-2">
                <span className="text-lg">📤</span>
                <h3 className="font-semibold text-gray-900">Share Article</h3>
              </div>
              <div className="flex gap-3">
                {shareLinks.map((link) =>
                  link.icon === 'link' ? (
                    <button
                      key={link.icon}
                      onClick={() => {
                        navigator.clipboard.writeText(currentUrl);
                        setCopied(true);
                        setTimeout(() => setCopied(false), 2000);
                      }}
                      className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 transition-all duration-200 hover:border-blue-200 hover:bg-blue-50 hover:text-blue-600"
                      title="Copy link"
                    >
                      <span className="text-sm font-medium">{copied ? '✓' : '🔗'}</span>
                    </button>
                  ) : (
                    <a
                      key={link.icon}
                      href={link.url(title, currentUrl)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex h-10 w-10 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 transition-all duration-200 hover:border-blue-200 hover:bg-blue-50 hover:text-blue-600"
                      title={`Share on ${link.icon}`}
                    >
                      <span className="text-sm font-medium">
                        {link.icon === 'linkedin' ? 'in' : link.icon === 'twitter' ? '𝕏' : '✉'}
                      </span>
                    </a>
                  )
                )}
              </div>
            </div>
          </aside>
        </div>

        {/* Add smooth scrolling styles */}
        <style
          dangerouslySetInnerHTML={{
            __html: `
            html {
              scroll-behavior: smooth;
            }
            h1[id], h2[id], h3[id], h4[id] {
              scroll-margin-top: 2rem;
            }
            article :is(h1, h2, h3, h4, h5, h6) a {
              color: #2563eb;
              text-decoration: underline;
            }
          `,
          }}
        />
      </div>
    </Layout>
  );
};

export default BlogsDetailPage;
